import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { CheckCircle2, Play, Lock, BookMarked, ArrowLeft } from "lucide-react";

export default function SessionLibrary() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    Promise.all([api.get(`/children/${childId}`), api.get("/sessions")]).then(([c, s]) => {
      setChild(c.data); setSessions(s.data);
    });
  }, [childId]);

  if (!child) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;

  return (
    <Shell>
      <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> {child.name}'s journey</Link>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2" style={{ color: tribe.accent }}><BookMarked size={12} /> Session Library</div>
        <h1 className="font-display text-3xl font-bold text-navy mt-1">Revisit any past exercise</h1>
        <p className="text-ink-mid mt-2">Every session {child.name} has already unlocked stays open forever. Reopen a favorite — no gates, no timers, just practice.</p>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {sessions.map((s) => {
          const done = s.number < child.current_session;
          const current = s.number === child.current_session;
          const locked = s.number > child.current_session;
          const tileStyle = locked
            ? { borderColor: `${tribe.accent}20`, opacity: 0.5 }
            : current
            ? { borderTop: `3px solid ${tribe.accent}`, borderColor: `${tribe.accent}40` }
            : done
            ? { borderColor: `${tribe.accent}40` }
            : { borderColor: `${tribe.accent}20` };
          return (
            <div key={s.number} style={tileStyle} className={`rounded-3xl border p-5 transition-all ${locked ? "" : "hover:-translate-y-1"} bg-warm-white`} data-testid={`library-tile-${s.number}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Session {s.number}</div>
                {done && <CheckCircle2 size={16} style={{ color: tribe.accent }} />}
                {current && <Play size={16} style={{ color: tribe.accent }} />}
                {locked && <Lock size={16} className="text-ink-soft" />}
              </div>
              <div className="font-display font-bold text-navy mt-2 text-lg">{s.title}</div>
              <div className="text-xs text-ink-mid mt-1">{s.focus}</div>
              <div className="mt-3 text-xs text-ink-soft">
                {s.active.length} active · {s.passive.length} passive
              </div>
              <div className="mt-4">
                {locked ? (
                  <div className="text-xs text-ink-soft italic">Unlocks after completing session {s.number - 1}</div>
                ) : (
                  <Link to={`/child/${childId}/session/${s.number}`} data-testid={`library-open-${s.number}`} className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: tribe.accent }}>
                    {done ? "Revisit" : "Open"} <Play size={12} />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
